import { Boundaries } from "@planzone/core";
import { Event, events } from "@planzone/events";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { reduceProject } from "../../common/project";
import { State, Selector } from "./types";
import { createSelector } from "./selector";
import { matchRequest } from "./utils";
import { calculateAdvancement } from "../../utils/calculateAdvancement";

const initialState: State = [];

function handleRequest(
  state: State,
  { filter, sort, size, index }: Boundaries["retrieveProjects"]["request"],
): State {
  const stateIndex = matchRequest(state, filter, sort, size);

  return stateIndex !== -1
    ? [
        ...state.slice(0, stateIndex),
        {
          ...state[stateIndex],
          request: setRequestStateToPending(),
        },
        ...state.slice(stateIndex + 1),
      ]
    : [
        ...state,
        {
          filter,
          sort,
          size,
          index,
          canLoadMore: true,
          projects: [],
          request: setRequestStateToPending(),
        },
      ];
}

function handleSuccess(
  state: State,
  {
    filter,
    sort,
    size,
    index,
    projects,
  }: Boundaries["retrieveProjects"]["success"],
): State {
  const stateIndex = matchRequest(state, filter, sort, size);

  return stateIndex !== -1
    ? [
        ...state.slice(0, stateIndex),
        {
          ...state[stateIndex],
          index: index + 1,
          canLoadMore: projects.length >= size,
          projects:
            index === 0
              ? projects
              : [
                  ...state[stateIndex].projects.slice(0, index * size),
                  ...projects,
                  ...state[stateIndex].projects.slice(index * size + size),
                ],
          request: setRequestStateToSuccess(),
        },
        ...state.slice(stateIndex + 1),
      ]
    : [
        ...state,
        {
          filter,
          sort,
          size,
          index: index + 1,
          canLoadMore: projects.length >= size,
          projects,
          request: setRequestStateToSuccess(),
        },
      ];
}

function handleFailure(
  state: State,
  { filter, sort, size, index }: Boundaries["retrieveProjects"]["failure"],
): State {
  const stateIndex = matchRequest(state, filter, sort, size);

  return stateIndex !== -1
    ? [
        ...state.slice(0, stateIndex),
        {
          ...state[stateIndex],
          request: setRequestStateToFailure(),
        },
        ...state.slice(stateIndex + 1),
      ]
    : [
        ...state,
        {
          filter,
          sort,
          size,
          index,
          canLoadMore: true,
          projects: [],
          request: setRequestStateToFailure(),
        },
      ];
}

function handleProjectRemoval(
  state: State,
  project: Boundaries["removeProject"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    projects: req.projects.filter(({ uuid }) => uuid !== project.uuid),
  }));
}

function handleTransferProjects(): State {
  return [];
}

function handleCreateProject(): State {
  return [];
}

function handleDuplicateProject(): State {
  return [];
}

function propagateEvent(state = initialState, event: Event): State {
  const project =
    event.type === events.createTaskSucceeded
      ? event.data.project
      : event.type === events.createKanbanTaskSucceeded
        ? event.data.project
        : event.type === events.createListSucceeded
          ? event.data.project
          : event.type === events.createMilestoneSucceeded
            ? event.data.project
            : event.type === events.createEventSucceeded
              ? event.data.project
              : null;

  return state.map((req) => ({
    ...req,
    projects: project
      ? req.projects.map((p) =>
          p.uuid === project.uuid && "numberOfElements" in p
            ? {
                ...reduceProject(p, event),
                numberOfElements: (p.numberOfElements as number) + 1,
              }
            : reduceProject(p, event),
        )
      : req.projects.map((project) => reduceProject(project, event)),
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveProjectsRequested]: handleRequest,
      [events.retrieveProjectsSucceeded]: handleSuccess,
      [events.retrieveProjectsFailed]: handleFailure,
      [events.removeProjectSucceeded]: handleProjectRemoval,
      [events.transferProjectsSucceeded]: handleTransferProjects,
      [events.createProjectSucceeded]: handleCreateProject,
      [events.useProjectModelSucceeded]: handleCreateProject,
      [events.duplicateProjectSucceeded]: handleDuplicateProject,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
